/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApllicationPackDetailsPage component
 * ---------------------------
 * It fetches and displays detailed information
 * about a specific application package based on the ID from URL parameters.
 * Error handling: Displays a simple error message box with a user-friendly
 * message if fetching the data fails (e.g. network error, 404 not found).
 * Loading state shows a spinner while waiting for API response.
 * 
 * For nested objects likedomain, and category,
 * it displays their 'name' property instead of the whole object.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { useParams, useLocation } from 'react-router-dom';
import APIHelper from '../api/APIHelper';
import MessageBox from '../components/MessageBox';
import '../styles/ApplicationDetailsPage.css';
import envConfig from "../config/envConfig";
import { useAlertDialog } from '../components/common/AlertDialogContext';

import ProgressBarDialog from '../components/common/ProgressBar';
import { useQuery } from '@tanstack/react-query';
import ClientKeySecretDialog from '../components/common/ClientKeySecretDialog';

const statusColors = {
    draft: "orange",
    published: "green"
};

const ATTRIBUTES = [
    { key: 'domain', label: 'Domain', icon: 'bi-globe' },
    { key: 'category', label: 'Category', icon: 'bi-tag' },
    { key: 'status', label: 'Status', icon: 'bi-circle-fill', colorFromStatus: true, formatter: v => v.charAt(0).toUpperCase() + v.slice(1) },
    { key: 'created_at', label: 'Created At', icon: 'bi-clock-history', formatter: formatDate },
    { key: 'updated_at', label: 'Updated At', icon: 'bi-arrow-repeat', formatter: formatDate }
];

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export default function ApllicationPackDetailsPage() {
    const { theme } = useTheme();
    const { id } = useParams();
    const location = useLocation();
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorInfo, setErrorInfo] = useState(null);
    const [installing, setInstalling] = useState(false);
    const [toggling, setToggling] = useState(false);
    const { showAlertDialog } = useAlertDialog();

    const isMyPackage = location.pathname.includes('/my-packages');

    const [cksOpen, setCksOpen] = useState(false);
    const [clientKey, setClientKey] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [setupRequired, setSetupRequired] = useState(null);

    const handleInstall = () => {
        showAlertDialog({
            title: "Confirm Installation",
            message: "Are you sure you want to install the package?",
            type: "info",
            okLabel: "Install",
            cancelLabel: "Cancel",
            showCancel: true,
            onOk: async () => {
                setInstalling(true);
                try {

                    const payload = {
                        application_pack_ids: [app.id],
                    };

                    const response = await apiHelper.apiCall('/arcee/application-pack/install/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        const errData = await response.json();
                        showAlertDialog({
                            title: "Installation Failed",
                            message: errData.message || response.statusText,
                            type: "error",
                            okLabel: "Retry",
                            cancelLabel: "Close",
                            showCancel: true,
                            onOk: () => handleInstall(), // Retry installation
                            onCancel: () => window.location.reload(), // Close and reload
                        });
                        return;
                    }

                    const data = await response.json();
                    const installError = data.results.find(r => r.error);
                    if (installError) {
                        showAlertDialog({
                            title: "Installation Failed",
                            message: installError.error,
                            type: "error",
                            okLabel: "Retry",
                            cancelLabel: "Close",
                            showCancel: true,
                            onOk: () => handleInstall(), // Retry installation
                            onCancel: () => window.location.reload(), // Close and reload
                        });
                        return;
                    }

                    const result = data.results[0];
                    const sr = result?.setup_required || null;
                    if (result && result.client_id && result.client_secret) {
                        setClientKey(result.client_id);
                        setClientSecret(result.client_secret);
                        setSetupRequired(sr);
                        setCksOpen(true);
                    } else {
                        const successMessage = sr
                            ? `Package installed successfully!\n\n⚠️ ${sr.message}`
                            : "Package installed successfully!";
                        showAlertDialog({
                            title: "Success",
                            message: successMessage,
                            type: sr ? "warning" : "success",
                            okLabel: "Close",
                            showCancel: false,
                            onOk: () => window.location.reload(),
                        });
                    }
                } catch (error) {
                    console.error('Install error:', error);
                    showAlertDialog({
                        title: "Error",
                        message: "An error occurred during installation. Please try again later.",
                        type: "error",
                        okLabel: "Retry",
                        cancelLabel: "Close",
                        showCancel: true,
                        onOk: () => handleInstall(), // Retry installation
                        onCancel: () => window.location.reload(), // Close and reload
                    });
                } finally {
                    setInstalling(false);
                }
            },
            onCancel: () => {
                // Do nothing on cancel
            }
        });
    };

    const handleToggleStatus = () => {
        const newStatus = app.status === 'draft' ? 'published' : 'draft';
        const actionText = newStatus === 'published' ? 'activate' : 'deactivate';

        showAlertDialog({
            title: `Confirm ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
            message: `Are you sure you want to ${actionText} the package "${app.name}"?`,
            type: "info",
            okLabel: actionText.charAt(0).toUpperCase() + actionText.slice(1),
            cancelLabel: "Cancel",
            showCancel: true,
            onOk: async () => {
                setToggling(true);
                try {
                    const payload = {
                        status: newStatus,
                    };

                    const response = await apiHelper.apiCall(`/arcee/application-pack/${app.id}/toggle-status/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        const errData = await response.json();
                        showAlertDialog({
                            title: "Update Failed",
                            message: errData.error || errData.message || response.statusText,
                            type: "error",
                            okLabel: "Close",
                            cancelLabel: "Close",
                            showCancel: false,
                        });
                        return;
                    }

                    const data = await response.json();

                    showAlertDialog({
                        title: "Success",
                        message: data.message || `Package ${actionText}d successfully!`,
                        type: "success",
                        okLabel: "Close",
                        showCancel: false,
                        onOk: () => {
                            window.location.reload();
                        },
                    });

                } catch (error) {
                    console.error('Toggle error:', error);
                    showAlertDialog({
                        title: "Error",
                        message: "An error occurred while updating the status. Please try again later.",
                        type: "error",
                        okLabel: "Close",
                        showCancel: false,
                    });
                } finally {
                    setToggling(false);
                }
            },
            onCancel: () => {
                // Do nothing on cancel
            }
        });
    };

    const packQueryKey = ['applicationPack', id];

    const {
        data: packData,
        error: packError,
        isLoading: packLoading,
        isFetching: packFetching,
    } = useQuery({
        queryKey: packQueryKey,
        queryFn: async ({ signal }) => {
            if (!id) return null;
            const url = `/arcee/application-pack/details/${id}/`;
            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });

            if (!resp.ok) {
                let errBody = null;
                try { errBody = await resp.json(); } catch (e) { /* ignore */ }
                const errMsg = (errBody && errBody.error) ? errBody.error : `Failed to load application pack (${resp.status})`;
                throw new Error(errMsg);
            }

            return resp.json();
        },
        enabled: !!id,
        staleTime: 30_000,
        retry: 1,
    });

    useEffect(() => {
        if (packLoading) {
            setLoading(true);
            setErrorInfo(null);
            return;
        }

        if (packError) {
            setLoading(false);
            setApp(null);
            setErrorInfo({ message: packError.message || 'Failed to load application pack.', type: 'error' });
            return;
        }

        if (packData) {
            setApp(packData);
            setLoading(false);
            setErrorInfo(null);
        }
    }, [packLoading, packData, packError]);

    const gradientTopBar = {
        width: '100%',
        height: '8px',
        borderTopLeftRadius: theme.cardRadius,
        borderTopRightRadius: theme.cardRadius,
        background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div
                    className="spinner-border"
                    style={{ color: theme.primary, width: '3rem', height: '3rem' }}
                    role="status"
                >
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (errorInfo) {
        return (
            <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
                <MessageBox messageType={errorInfo.type || 'error'} message={errorInfo.message || 'Something went wrong. Please try again.'} />
            </div>
        );
    }

    if (!app) return null;

    return (
        <div className="app-details-bg min-vh-100 py-5 px-2">
            <div className="container">
                <div className="row gx-5 gy-4 justify-content-center">
                    <div className="col-12 col-lg-7 mb-4 mb-lg-0">
                        <div className="shadow app-details-card" style={{
                            borderRadius: theme.cardRadius,
                            background: '#fff',
                            boxShadow: '0 4px 24px rgba(65,70,150,0.13)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={gradientTopBar} />
                            {app.banner_image && (
                                <div className="app-banner-img" style={{
                                    height: 150,
                                    width: '100%',
                                    background: `url('${app.banner_image}') center/cover no-repeat`,
                                    borderTopLeftRadius: theme.cardRadius,
                                    borderTopRightRadius: theme.cardRadius,
                                    marginTop: '-1px'
                                }} />
                            )}
                            <div className="p-4">
                                <div className="d-flex align-items-center mb-1 gap-3">
                                    <div className="fw-bold fs-3" style={{ color: theme.primary }}>
                                        {app.name}
                                    </div>
                                    {app.version && (
                                        <span
                                            style={{
                                                fontSize: '14px',
                                                padding: '4px 12px',
                                                borderRadius: '30px',
                                                fontWeight: 600,
                                                background: '#f1f5f9',
                                                color: '#475569',
                                                display: 'inline-block'
                                            }}
                                        >
                                            v{app.version}
                                        </span>
                                    )}
                                </div>

                                <div className="d-flex gap-4 mt-3 mb-4">
                                    {isMyPackage ? (
                                        <ActionButton
                                            label={app.status === 'published' ? 'In-active' : 'Active'}
                                            icon={app.status === 'published' ? 'bi-eye-slash' : 'bi-eye'}
                                            color={app.status === 'published' ? '#f59e0b' : '#10b981'}
                                            onClick={handleToggleStatus}
                                        />
                                    ) : !app.is_owner ? (
                                        <ActionButton
                                            label={app.installed ? "Installed" : "Install"}
                                            icon={app.installed ? "bi-check-circle-fill" : "bi-cloud-arrow-down"}
                                            color={app.installed ? "green" : theme.primary}
                                            disabled={app.installed}
                                            onClick={!app.installed ? handleInstall : undefined}
                                        />
                                    ) : null}
                                </div>


                                {app.description && (
                                    <div style={{ color: '#555', fontSize: 17, lineHeight: 1.55 }}>
                                        {app.description}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-3">
                        <div style={gradientTopBar} />
                        <div className="shadow app-details-card p-4" style={{
                            borderRadius: theme.cardRadius,
                            background: '#fff',
                            boxShadow: '0 4px 20px rgba(90,70,150,0.12)'
                        }}>
                            <div className="mb-3 text-muted fw-semibold" style={{ letterSpacing: '.04em' }}>
                                Details
                            </div>

                            {app.logo && (
                                <div className="d-flex justify-content-center mb-3">
                                    <img
                                        src={app.logo}
                                        alt={app.name}
                                        style={{
                                            height: 68,
                                            width: 68,
                                            borderRadius: theme.cardRadius,
                                            background: '#f2f6fb',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}

                            <ul className="list-unstyled app-attr-list">
                                {ATTRIBUTES.map(attr => {
                                    let value = app[attr.key];
                                    // If nested object with 'name', use the name instead
                                    if (typeof value === 'object' && value !== null && 'name' in value) {
                                        value = value.name;
                                    }
                                    if (value == null || value === '') return null;
                                    const color = attr.colorFromStatus ? statusColors[value] : theme.primary;
                                    return (
                                        <li key={attr.key} className="mb-3 d-flex align-items-center">
                                            <i className={`bi ${attr.icon} me-2 flex-shrink-0`} style={{ color, fontSize: 20 }} />
                                            <span className="fw-semibold me-2" style={{ color: theme.primary }}>
                                                {attr.label}:
                                            </span>
                                            <span>{attr.formatter ? attr.formatter(value) : value}</span>
                                        </li>
                                    );

                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <ProgressBarDialog
                open={installing}
                text_message="Installing package..."
                cancellable={false}
            />
            <ProgressBarDialog
                open={toggling}
                text_message="Updating status..."
                cancellable={false}
            />
            <ClientKeySecretDialog
                open={cksOpen}
                onClose={() => {
                    setCksOpen(false);
                    if (setupRequired) {
                        showAlertDialog({
                            title: "Webhook Setup Required",
                            message: setupRequired.message,
                            type: "warning",
                            okLabel: "Got it",
                            showCancel: false,
                            onOk: () => window.location.reload(),
                        });
                    } else {
                        window.location.reload();
                    }
                }}
                clientKey={clientKey}
                clientSecret={clientSecret}
                canRegenerate={false}
                actionLabel="Close & Reload"
            />
        </div>
    );
}

function ActionButton({ label, icon, color, isDelete, disabled, onClick }) {
    return (
        <button
            type="button"
            className="border-0 bg-transparent d-flex align-items-center gap-2 px-1 py-2"
            style={{
                color,
                fontWeight: 500,
                fontSize: 15,
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.6 : 1
            }}
            onClick={disabled ? undefined : onClick}
        >
            <i className={`bi ${icon}`} style={{ fontSize: 20 }} />
            {label}
        </button>
    );
}


function formatDate(dateString) {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}
